/**
 * Creating a level's monsters (02 §5 Spawning), as the level set-up does it: entity-spawn.s D_39D2
 * reads each 3-byte entry, then $17BE lifts every monster to Y $15 for the drop-in, keeping its
 * spawn Y as the drop target. The level-start clear at $05F5 gives the rest of the state.
 */

import { makeBoss } from './boss';
import { initialMonsterAi, type MonsterAi } from './state';
import { DROP_START_Y, FACE_FRAMES, INTERVAL_BASE, INTERVAL_RAND, KIND_STATE, SUPORR_LAST_LEVEL } from './tuning';
import { rngOf } from '../random';
import type { LevelMonster, MonsterKind } from '../../levels/types';
import type { Monster, World } from '../types';

/**
 * Level data gives the 24x21 sprite's top-left in playfield pixels (tools/import-levels.mjs took
 * geon's sprite registers minus (24, 50)); D_39D2 computes the registers as (byte0 & $F8) + $14 and
 * (byte1 & $F8) + $15, so adding (24, 50) back gives them exactly (x = 4, y = 5 mod 8 for all 572).
 */
const SPRITE_FROM_DATA = { x: 24, y: 50 } as const;

function aiFor(world: World, lm: LevelMonster, slot: number, targetY: number): MonsterAi {
  const state = KIND_STATE[lm.kind];
  const faceFrames = FACE_FRAMES[state] ?? 4;
  // D_8638: the 6-bit delay x 2 ($39F2 `and #$3f; asl`).
  const a = initialMonsterAi(lm.delay * 2, targetY);
  a.slot = slot;
  a.faceFrames = faceFrames;
  // D_8520: facing left when flag bit 6 is set ($3A26: frame = $AB79[state], else 0).
  a.frame = lm.left ? faceFrames : 0;
  // D_85E8: the four motion bits (1 left, 2 right, 4 up / jump, 8 down), $3A0F..$3A1D.
  a.joy = lm.motion & 0x0f;
  // D_8688 / D_8660: toggle interval = $AB71[state] + rand(0..31), one roll per monster ($3A36).
  a.interval = ((INTERVAL_BASE[state] ?? 0) + rngOf(world).int(INTERVAL_RAND)) & 0xff;
  a.timer = a.interval;
  return a;
}

/**
 * The SUPORR chord (02 §9.5; L_45A7, the loop at L_45F2): the enemy tables of levels 1-99 are
 * rewritten in RAM, type 0 (Zen-chan) to 1 (Hidegons) and 6 (Mighta) to 5 (Drunk). Everything else
 * in the entry is kept.
 */
function suporrKind(world: World, kind: MonsterKind): MonsterKind {
  if (!world.options.suporr || world.level.n > SUPORR_LAST_LEVEL) return kind;
  return kind === 'zenChan' ? 'hidegons' : kind === 'mighta' ? 'drunk' : kind;
}

/** Create the level's monsters, lifted to the top of the screen, ready to drop in; on level 100 the boss. */
export function spawnLevelMonsters(world: World): void {
  world.level.monsters.forEach((data, slot) => {
    const lm: LevelMonster = { ...data, kind: suporrKind(world, data.kind) };
    const x = lm.x + SPRITE_FROM_DATA.x;
    const targetY = lm.y + SPRITE_FROM_DATA.y;
    const m: Monster = {
      id: world.nextId++, kind: lm.kind, x, y: Math.min(targetY, DROP_START_Y), facing: lm.left ? -1 : 1,
      state: 'dropIn', angry: false, frozen: false, bubbleId: -1, ai: aiFor(world, lm, slot, targetY),
    };
    world.monsters.push(m);
  });
  world.boss = makeBoss(world);
}
